import React from 'react';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useDispatch } from 'react-redux';
import { setCustomSeatNumber } from '../../db/actions';
import { ActionCreators as EtherealActionCreators } from '../../state/ethereal/actions';

/**
 * Click-to-edit seat number. Renders the current seat number; clicking opens
 * a small popover where the user can assign a custom number to this grid
 * space (mimicking the physical courtroom layout) or reset it to default.
 */
export default function SeatNumberEditor({
  caseId,
  mode,
  spaceNumber,
  seatNumber,
  isCustom = false,
  isConflict = false,
  defaultSeatNumber,
}) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [value, setValue] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const open = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setValue(String(seatNumber));
    setAnchorEl(e.currentTarget);
  };

  const close = () => setAnchorEl(null);

  const apply = async (nextValue) => {
    if (saving) return;
    setSaving(true);
    try {
      const result = await setCustomSeatNumber(caseId, mode, spaceNumber, nextValue);
      close();
      if (result.conflicts.length > 0) {
        dispatch(EtherealActionCreators.addAlert(
          `Seat number ${result.conflicts.join(', ')} is now used by more than one seat. ` +
          'Conflicting seats are highlighted — relabel one of them to resolve.',
          'Duplicate seat numbers',
          'warning',
        ));
      }
    } catch (error) {
      dispatch(EtherealActionCreators.addAlert(
        error.message || 'Could not change the seat number.',
        'Seat number not changed',
        'error',
      ));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const parsed = parseInt(value, 10);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      dispatch(EtherealActionCreators.addAlert(
        'Seat numbers must be positive whole numbers.',
        'Invalid seat number',
        'error',
      ));
      return;
    }
    // Saving the default value is the same as resetting the override.
    apply(parsed === defaultSeatNumber ? null : parsed);
  };

  return (
    <>
      <Tooltip title={isConflict
        ? 'Duplicate seat number — click to change'
        : "Click to change this seat's number"}>
        <span
          onClick={open}
          style={{
            cursor: 'pointer',
            textDecoration: isCustom ? 'underline dotted' : 'none',
            textUnderlineOffset: 3,
            ...(isConflict ? {
              color: '#D32F2F',
              backgroundColor: 'rgba(211,47,47,0.12)',
              borderRadius: 3,
              padding: '0 4px',
            } : {}),
          }}
        >
          <b>{seatNumber}</b>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, width: 200 }}>
          <Typography variant="caption" color="textSecondary">
            Seat number for this space
            {isCustom ? ` (default: ${defaultSeatNumber})` : ''}
          </Typography>
          <TextField
            autoFocus
            size="small"
            type="number"
            inputProps={{ min: 1 }}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') close();
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            {isCustom ? (
              <Button size="small" disabled={saving} onClick={() => apply(null)}>
                Reset
              </Button>
            ) : <span />}
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={saving}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
